// ─────────────────────────────────────────────────────────────────────────────
// Practice reminders — daily-time bilateral-calm nudges.
//
// The user picks up to 4 times per day (e.g. 09:00 + 20:00) and up to 7
// days of the week. Preferences are persisted on the backend
// (`/api/practice-reminders`) so they survive device switches.
//
// Firing the notification is split by platform:
//   • iOS Capacitor build → @capacitor/local-notifications. Truly
//     persistent — the OS fires the alert even when the app is closed.
//   • Web browser        → Notification API + setTimeout. We only fire
//     when the tab is open near the scheduled minute; documented in the
//     UI so users understand the limitation.
//
// This module is defensive: everything no-ops when the platform lacks
// the capability. The UI reads `getCapabilities()` and adjusts copy.
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";
import logger from "./logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isNative = () => {
  try {
    // eslint-disable-next-line global-require
    const { Capacitor } = require("@capacitor/core");
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
};

const getLocalNotifications = () => {
  try {
    // eslint-disable-next-line global-require
    const mod = require("@capacitor/local-notifications");
    return mod?.LocalNotifications || null;
  } catch {
    return null;
  }
};

// Base notification ID range. Capacitor requires an int32; we allocate a
// stable id per (day-of-week, time-slot) so re-scheduling overwrites the
// prior alert instead of stacking duplicates.
// id = 10000 + slot*10 + day  → e.g. slot 2 on Wednesday → 10023.
const idFor = (slotIndex, dayIndex) => 10000 + slotIndex * 10 + dayIndex;

/** What can this platform do? Drives UI copy so we don't over-promise. */
export const getCapabilities = () => {
  if (isNative() && getLocalNotifications()) {
    return { kind: "native", persistent: true };
  }
  if (typeof window !== "undefined" && "Notification" in window) {
    return { kind: "web", persistent: false };
  }
  return { kind: "unsupported", persistent: false };
};

/** Ask the OS/browser for permission. Returns true iff granted. */
export const requestPermission = async () => {
  const caps = getCapabilities();
  if (caps.kind === "native") {
    const LN = getLocalNotifications();
    try {
      const perm = await LN.requestPermissions();
      // display can be "granted" / "denied" / "prompt"
      return perm?.display === "granted";
    } catch (e) {
      logger.warn("LocalNotifications permission request failed", e);
      return false;
    }
  }
  if (caps.kind === "web") {
    try {
      const perm = await window.Notification.requestPermission();
      return perm === "granted";
    } catch {
      return false;
    }
  }
  return false;
};

export const checkPermission = async () => {
  const caps = getCapabilities();
  if (caps.kind === "native") {
    const LN = getLocalNotifications();
    try {
      const perm = await LN.checkPermissions();
      return perm?.display || "prompt";
    } catch {
      return "prompt";
    }
  }
  if (caps.kind === "web") {
    return window.Notification?.permission || "default";
  }
  return "unsupported";
};

/** Fetch this user's saved schedule from the backend. */
export const fetchReminderPrefs = async () => {
  try {
    const res = await axios.get(`${API}/practice-reminders`);
    return res.data;
  } catch {
    return { enabled: false, times: [], days: [], message: "" };
  }
};

/** Persist a schedule to the backend (server validates). */
export const saveReminderPrefs = async (prefs) => {
  const res = await axios.put(`${API}/practice-reminders`, prefs);
  return res.data;
};

// ─── Native scheduling (iOS) ───────────────────────────────────────────────
// LocalNotifications supports weekly-recurring triggers by supplying
// { weekday, hour, minute } where weekday is 1..7 (Sunday=1). We convert
// our ISO 0..6 (Monday..Sunday) into that.
const isoToCapacitorWeekday = (isoDay) => {
  // iso: 0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri, 5=Sat, 6=Sun
  // capacitor: 1=Sun, 2=Mon, ..., 7=Sat
  const map = [2, 3, 4, 5, 6, 7, 1];
  return map[isoDay] ?? 2;
};

const scheduleNative = async (prefs) => {
  const LN = getLocalNotifications();
  if (!LN) return;
  // Cancel every id in the range we might have used previously.
  const toCancel = [];
  for (let s = 0; s < 4; s++) {
    for (let d = 0; d < 7; d++) {
      toCancel.push({ id: idFor(s, d) });
    }
  }
  try { await LN.cancel({ notifications: toCancel }); } catch { /* no-op */ }

  if (!prefs.enabled || !prefs.times?.length) return;

  const days = prefs.days?.length ? prefs.days : [0, 1, 2, 3, 4, 5, 6];
  const notifications = [];
  prefs.times.forEach((time, slotIndex) => {
    const [hh, mm] = time.split(":").map((n) => Number.parseInt(n, 10));
    days.forEach((isoDay) => {
      notifications.push({
        id: idFor(slotIndex, isoDay),
        title: "Bilateral Calm",
        body: prefs.message || "Two minutes of Bilateral Calm is waiting for you.",
        schedule: {
          on: { weekday: isoToCapacitorWeekday(isoDay), hour: hh, minute: mm },
          allowWhileIdle: true,
          repeats: true,
        },
        smallIcon: "ic_stat_icon_config_sample",
      });
    });
  });
  if (notifications.length) {
    try {
      await LN.schedule({ notifications });
    } catch (e) {
      logger.warn("LocalNotifications.schedule failed", e);
    }
  }
};

// ─── Web scheduling (browser) ──────────────────────────────────────────────
// Simple approach: while the tab is open, keep a single timer pointing at
// the *next* upcoming reminder. When it fires, we show a Notification and
// schedule the next one. This is deliberately not persistent — the UI
// copy tells the user "reminders fire while the app is open".
let webTimerId = null;
let webActivePrefs = null;

const nextFireTime = (prefs) => {
  if (!prefs?.enabled || !prefs.times?.length) return null;
  const days = prefs.days?.length ? new Set(prefs.days) : new Set([0, 1, 2, 3, 4, 5, 6]);
  const now = new Date();
  // Search up to 8 days out to find the next valid slot.
  for (let offset = 0; offset < 8; offset++) {
    const candidate = new Date(now);
    candidate.setDate(now.getDate() + offset);
    const isoDay = (candidate.getDay() + 6) % 7; // JS 0=Sun → ISO 0=Mon
    if (!days.has(isoDay)) continue;
    for (const t of prefs.times) {
      const [hh, mm] = t.split(":").map((n) => Number.parseInt(n, 10));
      const slot = new Date(candidate);
      slot.setHours(hh, mm, 0, 0);
      if (slot > now) return slot;
    }
  }
  return null;
};

const scheduleNextWeb = () => {
  if (webTimerId) {
    clearTimeout(webTimerId);
    webTimerId = null;
  }
  const prefs = webActivePrefs;
  const next = nextFireTime(prefs);
  if (!next) return;
  const delay = Math.max(0, next.getTime() - Date.now());
  // Cap at ~24h so long-running tabs still refresh their timer occasionally.
  const chunk = Math.min(delay, 24 * 60 * 60 * 1000);
  webTimerId = setTimeout(() => {
    if (delay > chunk) {
      // Not time yet — reschedule to check again.
      scheduleNextWeb();
      return;
    }
    try {
      if (window.Notification?.permission === "granted") {
        const n = new window.Notification("Bilateral Calm", {
          body: prefs.message || "Two minutes of Bilateral Calm is waiting for you.",
          icon: "/icon-192.webp",
          tag: "bc-practice-reminder",
        });
        n.onclick = () => {
          try {
            window.focus();
            window.location.assign("/dashboard");
          } catch { /* ignore */ }
        };
      }
    } catch (e) {
      logger.warn("Web notification fire failed", e);
    }
    // Chain to the next slot.
    scheduleNextWeb();
  }, chunk);
};

/** Apply a schedule (permission must already be granted). */
export const applySchedule = async (prefs) => {
  const caps = getCapabilities();
  if (caps.kind === "native") {
    await scheduleNative(prefs);
    return;
  }
  if (caps.kind === "web") {
    webActivePrefs = prefs;
    scheduleNextWeb();
  }
};

/** Cancel everything (called when the user disables reminders). */
export const cancelAll = async () => {
  const caps = getCapabilities();
  if (caps.kind === "native") {
    await scheduleNative({ enabled: false, times: [], days: [] });
    return;
  }
  if (caps.kind === "web" && webTimerId) {
    clearTimeout(webTimerId);
    webTimerId = null;
    webActivePrefs = null;
  }
};
